import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlarmClock, RefreshCw, Search, Package, Loader2, AlertTriangle,
  TrendingDown, Calendar, Warehouse, Tag, Building2, ShoppingCart,
} from "lucide-react";
import { toast } from "sonner";
import { smartMatch } from "../../utils/arabicSearch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

const THRESHOLDS = [
  { d: 7, label: "٧ أيام" },
  { d: 14, label: "١٤ يوم" },
  { d: 30, label: "٣٠ يوم" },
  { d: 60, label: "٦٠ يوم" },
];

// ─── Product row card ───────────────────────────────────────
const ProductCard = ({ p, thresholdDays }) => {
  const isNever = !!p.never_sold;
  const days = Number(p.days_since_last_sold || 0);

  // Severity: never-sold OR > 3× threshold = خطر, > threshold = تحذير
  const sev = isNever || days > thresholdDays * 3 ? "danger"
            : days > thresholdDays * 2 ? "warn"
            : "info";
  const sevCls = {
    danger: { border: "border-[#E85D45]", bg: "bg-[#FEE2E2]", txt: "text-[#B91C1C]" },
    warn: { border: "border-[#F5A623]", bg: "bg-[#FFF8EC]", txt: "text-[#a06e10]" },
    info: { border: "border-[#3B82F6]", bg: "bg-[#DBEAFE]", txt: "text-[#1E40AF]" },
  }[sev];

  return (
    <div
      data-testid={`slow-prod-${p.id}`}
      className={`bg-white border-2 ${sevCls.border} rounded-2xl p-3 flex gap-3 items-start`}
    >
      {/* Image */}
      <div className="shrink-0">
        {p.image_path ? (
          <img
            src={`${API}/files/${p.image_path}`}
            alt={p.name_ar}
            className="w-20 h-20 rounded-xl object-cover border border-[#E4EBE6]"
          />
        ) : (
          <div className="w-20 h-20 bg-[#F3F6F4] rounded-xl flex items-center justify-center">
            <Package className="w-8 h-8 text-[#5C6E64]" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <p className="font-bold text-sm text-[#111814] truncate">{p.name_ar}</p>
          {/* Prominent stagnation badge */}
          <div
            className={`shrink-0 rounded-lg px-2 py-1 text-center ${sevCls.bg} ${sevCls.txt} border border-current/30`}
          >
            <p className="text-[9px] font-bold leading-none">
              {isNever ? "ما تباع أبداً" : "آخر بيع قبل"}
            </p>
            <p className="text-sm font-black leading-none mt-0.5" dir="ltr">
              {days} يوم
            </p>
          </div>
        </div>

        {/* Product meta chips */}
        <div className="flex flex-wrap gap-1 text-[10px] mb-1.5">
          {p.brand_name && (
            <span className="bg-[#F0F9FF] text-[#1E40AF] rounded-full px-2 py-0.5 flex items-center gap-0.5">
              <Building2 className="w-2.5 h-2.5" /> {p.brand_name}
            </span>
          )}
          {p.category && (
            <span className="bg-[#F3F6F4] text-[#5C6E64] rounded-full px-2 py-0.5 flex items-center gap-0.5">
              <Tag className="w-2.5 h-2.5" /> {p.category}
            </span>
          )}
          {p.weight_per_piece && (
            <span className="bg-[#FFF8EC] text-[#a06e10] rounded-full px-2 py-0.5" dir="ltr">
              {p.weight_per_piece}
            </span>
          )}
          {p.units_per_carton && (
            <span className="bg-[#FFF8EC] text-[#a06e10] rounded-full px-2 py-0.5">
              <span dir="ltr">{p.units_per_carton}</span> بالكرتون
            </span>
          )}
        </div>

        {/* Prices + stock grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[10px]">
          <MetricBox label="سعر الشراء" val={`${fmt(p.cost_price)} د.ع`} color="#F5A623" />
          <MetricBox label="سعر A+" val={`${fmt(p.price)} د.ع`} color="#15803D" />
          <MetricBox label="سعر B+" val={`${fmt(p.wholesale_price)} د.ع`} color="#7C3AED" />
          <MetricBox
            label="المخزن"
            val={`${fmt(p.stock)}`}
            color={p.stock === 0 ? "#B91C1C" : p.stock <= 5 ? "#F5A623" : "#1E3A5F"}
          />
        </div>

        {/* Bottom row: last sold date + total sold qty */}
        <div className="flex flex-wrap gap-1.5 mt-1.5 text-[10px] text-[#5C6E64]">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            آخر بيع: <b className="text-[#111814]" dir="ltr">{fmtDate(p.last_sold_at)}</b>
          </span>
          <span className="flex items-center gap-1">
            <ShoppingCart className="w-3 h-3" />
            بيع كلّي:{" "}
            <b className="text-[#111814]" dir="ltr">
              {fmt(p.total_sold_qty)}
            </b>
          </span>
        </div>
      </div>
    </div>
  );
};

const MetricBox = ({ label, val, color }) => (
  <div className="bg-[#F8FAFC] border border-[#E4EBE6] rounded-lg px-1.5 py-1 text-center">
    <p className="text-[9px] text-[#5C6E64] leading-none">{label}</p>
    <p className="text-[11px] font-black mt-0.5" style={{ color }} dir="ltr">{val}</p>
  </div>
);

// ─── Main tab ──────────────────────────────────────────────
const SlowMovingProductsTab = () => {
  const [thresholdDays, setThresholdDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [neverOnly, setNeverOnly] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(
        `${API}/admin/slow-moving-products`,
        { params: { days: thresholdDays } }
      );
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحميل المواد المتأخرة");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [thresholdDays]);

  const filtered = useMemo(() => {
    if (!data?.items) return [];
    let list = data.items;
    if (neverOnly) list = list.filter((p) => p.never_sold);
    if (q.trim()) {
      list = list.filter((p) =>
        smartMatch(`${p.name_ar || ""} ${p.name_en || ""} ${p.brand_name || ""} ${p.category || ""}`, q)
      );
    }
    return list;
  }, [data, q, neverOnly]);

  const stats = useMemo(() => {
    if (!data?.items) return { total: 0, never: 0, oldest: 0 };
    const never = data.items.filter((p) => p.never_sold).length;
    const oldest = data.items.reduce((m, p) => Math.max(m, p.days_since_last_sold || 0), 0);
    return { total: data.items.length, never, oldest };
  }, [data]);

  return (
    <div className="space-y-3" data-testid="slow-moving-tab" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#E85D45] to-[#F5A623] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <AlarmClock className="w-5 h-5" />
          <h3 className="font-bold text-lg">المواد المتأخرة بالبيع</h3>
        </div>
        <p className="text-xs opacity-95">
          المواد اللي ما تم شراؤها (من زبون أو مشرف أو بيع مباشر) بخلال آخر
          <b className="mx-1">{thresholdDays}</b>
          يوم — حتى تعرف تعالجها (عرض / خصم / إعلان / إزالة).
        </p>
      </div>

      {/* Threshold selector */}
      <div className="bg-white border-2 border-[#E4EBE6] rounded-2xl p-3">
        <p className="text-xs font-bold text-[#1E3A5F] mb-2">فترة الركود</p>
        <div className="flex flex-wrap gap-1.5">
          {THRESHOLDS.map((t) => (
            <button
              key={t.d}
              onClick={() => setThresholdDays(t.d)}
              data-testid={`slow-threshold-${t.d}`}
              className={`text-xs font-bold py-1.5 px-3 rounded-full transition-colors ${
                thresholdDays === t.d
                  ? "bg-[#E85D45] text-white"
                  : "bg-[#F3F6F4] text-[#5C6E64] hover:bg-[#E4EBE6]"
              }`}
            >
              {t.label}
            </button>
          ))}
          <button
            onClick={fetchData}
            data-testid="slow-refresh"
            className="text-xs font-bold py-1.5 px-3 rounded-full bg-[#1E3A5F] hover:bg-[#152a45] text-white flex items-center gap-1 mr-auto"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> تحديث
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-gradient-to-l from-[#B91C1C] to-[#DC2626] text-white rounded-2xl p-3 text-center">
          <p className="text-[10px] opacity-90">إجمالي المتأخرة</p>
          <p className="text-2xl font-black" dir="ltr">{fmt(stats.total)}</p>
        </div>
        <div className="bg-gradient-to-l from-[#F5A623] to-[#dc9320] text-white rounded-2xl p-3 text-center">
          <p className="text-[10px] opacity-90">ما تباع أبداً</p>
          <p className="text-2xl font-black" dir="ltr">{fmt(stats.never)}</p>
        </div>
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-3 text-center">
          <p className="text-[10px] opacity-90">أطول ركود (يوم)</p>
          <p className="text-2xl font-black" dir="ltr">{fmt(stats.oldest)}</p>
        </div>
      </div>

      {/* Search + never-only filter */}
      <div className="bg-white border-2 border-[#E4EBE6] rounded-2xl p-3 space-y-2">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="slow-search"
            placeholder="ابحث باسم المادة / الشركة / الصنف..."
            className="w-full pr-9 pl-4 py-2 rounded-xl border-2 border-[#E4EBE6] bg-white text-sm focus:outline-none focus:border-[#E85D45]"
          />
        </div>
        <label
          data-testid="slow-never-only"
          className={`flex items-center gap-2 cursor-pointer rounded-xl px-3 py-2 border-2 transition-colors ${
            neverOnly ? "bg-[#FEE2E2] border-[#E85D45]" : "bg-[#F9FAFB] border-[#E4EBE6]"
          }`}
        >
          <input
            type="checkbox"
            checked={neverOnly}
            onChange={(e) => setNeverOnly(e.target.checked)}
            className="w-5 h-5 accent-[#E85D45] cursor-pointer"
          />
          <span className={`text-sm font-bold ${neverOnly ? "text-[#B91C1C]" : "text-[#1E3A5F]"}`}>
            اعرض فقط المواد اللي ما تباع أبداً
          </span>
        </label>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin inline text-[#E85D45]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#ECFDF5] border-2 border-[#22C55E] rounded-2xl p-8 text-center">
          <div className="w-14 h-14 bg-white rounded-full mx-auto mb-2 flex items-center justify-center">
            <TrendingDown className="w-7 h-7 text-[#15803D]" />
          </div>
          <p className="text-sm font-bold text-[#15803D]">
            {(data?.count || 0) === 0
              ? `🎉 ممتاز! كل المواد بيعت بخلال آخر ${thresholdDays} يوم.`
              : "لا توجد نتائج مطابقة للفلاتر"}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="slow-list">
          {filtered.map((p) => (
            <ProductCard key={p.id} p={p} thresholdDays={thresholdDays} />
          ))}
        </div>
      )}

      {/* Legend */}
      {filtered.length > 0 && (
        <div className="bg-[#F8FAFC] border border-[#E4EBE6] rounded-xl p-2 text-[10px] text-[#5C6E64] flex flex-wrap gap-x-3 gap-y-1">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded bg-[#FEE2E2] border border-[#E85D45]" />
            خطر (ما تباع أبداً أو ركود شديد)
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded bg-[#FFF8EC] border border-[#F5A623]" />
            تحذير (ركود متوسط)
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded bg-[#DBEAFE] border border-[#3B82F6]" />
            متأخرة (ما بيعت بالفترة المختارة)
          </span>
        </div>
      )}

      {/* Unused import silencer for lint */}
      <span className="hidden">
        <AlertTriangle /> <Warehouse />
      </span>
    </div>
  );
};

export default SlowMovingProductsTab;
